import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [properties, setProperties] = useState([]);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth data on mount
    const storedUser = localStorage.getItem('hms_user');
    const storedTenant = localStorage.getItem('hms_tenant');
    const storedProperties = localStorage.getItem('hms_properties');
    const storedCurrentProperty = localStorage.getItem('hms_current_property');

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (storedTenant) {
      setTenant(JSON.parse(storedTenant));
    }
    if (storedProperties) {
      setProperties(JSON.parse(storedProperties));
    }
    if (storedCurrentProperty) {
      setCurrentProperty(JSON.parse(storedCurrentProperty));
    }
    setIsLoading(false);
  }, []);

  const login = (data) => {
    localStorage.setItem('hms_token', data.access_token);
    localStorage.setItem('hms_user', JSON.stringify(data.user));
    localStorage.setItem('hms_tenant', JSON.stringify(data.tenant));
    localStorage.setItem('hms_properties', JSON.stringify(data.properties));
    
    setUser(data.user);
    setTenant(data.tenant);
    setProperties(data.properties);
    
    // Set first property as current
    if (data.properties.length > 0) {
      const firstProp = data.properties[0];
      localStorage.setItem('hms_current_property', JSON.stringify(firstProp));
      setCurrentProperty(firstProp);
    }
  };

  const logout = () => {
    localStorage.removeItem('hms_token');
    localStorage.removeItem('hms_user');
    localStorage.removeItem('hms_tenant');
    localStorage.removeItem('hms_properties');
    localStorage.removeItem('hms_current_property');
    setUser(null);
    setTenant(null);
    setProperties([]);
    setCurrentProperty(null);
  };

  const switchProperty = (property) => {
    localStorage.setItem('hms_current_property', JSON.stringify(property));
    setCurrentProperty(property);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.roles?.includes('SUPER_ADMIN')) return true;
    return user.permissions?.includes(permission);
  };

  const hasRole = (role) => {
    if (!user) return false;
    return user.roles?.includes(role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        properties,
        currentProperty,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        switchProperty,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
